import { FormControl, Grid, Stack } from "@mui/material";
import { type FC, useEffect, useRef, useState } from "react";
import { NumericFormat } from "react-number-format";

import {
  amountPreselectedValues,
  MAX_AMOUNT,
} from "~/feature/event/components/MarketCalculator/components/MarketAmountField/constants";
import {
  useMarketCalculator,
  useMarketCalculatorActions,
} from "~/feature/event/providers/MarketCalculatorProvider";
import { useDebouncedValue } from "~/hooks/useDebouncedValue";

import * as Styled from "./MarketAmount.styles";

interface IMarketAmountFieldProps {
  line?: boolean;
  hidePresets?: boolean;
}

export const MarketAmountField: FC<IMarketAmountFieldProps> = ({
  line = false,
  hidePresets = false,
}) => {
  const { betAmount } = useMarketCalculator();
  const { setBetAmount } = useMarketCalculatorActions();
  const [amount, setAmount] = useState<number>(betAmount);
  const debouncedAmount = useDebouncedValue(amount);
  const lastSyncedAmount = useRef(betAmount);

  useEffect(() => {
    lastSyncedAmount.current = debouncedAmount;
    setBetAmount(debouncedAmount);
  }, [debouncedAmount, setBetAmount]);

  useEffect(() => {
    if (betAmount !== lastSyncedAmount.current) {
      lastSyncedAmount.current = betAmount;
      setAmount(betAmount);
    }
  }, [betAmount]);

  return (
    <FormControl
      fullWidth
      sx={{
        maxWidth: { xs: "100%", md: 280, lg: 320, xl: 400 },
        minWidth: 0,
        flex: "1 1 0",
      }}
    >
      <Stack
        sx={{
          width: "100%",
          gap: 0.5
        }}>
        <Styled.Label
          variant="caption"
          color="text.secondary"
          $line={line}
          sx={{ fontWeight: 600, textTransform: "uppercase" }}
        >
          Amount
        </Styled.Label>
        <NumericFormat
          customInput={Styled.Input}
          id="amount"
          $line={line}
          value={amount}
          thousandSeparator
          allowNegative={false}
          decimalScale={0}
          isAllowed={({ floatValue }) =>
            floatValue === undefined || floatValue <= MAX_AMOUNT
          }
          onValueChange={({ floatValue }) => setAmount(floatValue ?? 0)}
          startAdornment={
            <Styled.InputAdornment position="start">$</Styled.InputAdornment>
          }
          inputProps={{
            inputMode: "numeric",
          }}
          placeholder="100"
        />
        {!hidePresets && (
          <Grid container spacing={2}>
            {amountPreselectedValues.map((value) => (
              <Grid key={value} size={3}>
                <Styled.PresetButton
                  type="button"
                  $active={value === amount}
                  $line={line}
                  onClick={() => setAmount(value)}
                >
                  ${value}
                </Styled.PresetButton>
              </Grid>
            ))}
          </Grid>
        )}
      </Stack>
    </FormControl>
  );
};
